import * as z from "zod";
import {
  type BuildContext,
  type CycleCount,
  type CycleCountProduct,
  daysSinceLive,
  groupProductsByLocation,
  isStaleBeyond,
} from "../cycle-count-core";

export const abcMetricSchema = z.enum([
  "revenue",
  "velocity",
  "margin",
  "units_on_hand",
]);
export type AbcMetric = z.infer<typeof abcMetricSchema>;

export const abcRuleDataSchema = z.object({
  a_days: z.int().min(1),
  b_days: z.int().min(1),
  c_days: z.int().min(1),
  classification: z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("smart") }),
    z.object({
      mode: z.literal("threshold"),
      metric: abcMetricSchema,
      a_pct: z.int().min(1).max(98),
      b_pct: z.int().min(1).max(98),
      c_pct: z.int().min(1).max(98),
    }),
  ]),
});
export type AbcRuleData = z.infer<typeof abcRuleDataSchema>;

export interface AbcRule {
  type: "abc";
  data: AbcRuleData;
  liveAt: Date;
}

export type Tier = "A" | "B" | "C";

const DEFAULT_SMART_PERCENTS = { aPct: 20, bPct: 30, cPct: 50 };

interface TierPercents {
  aPct: number;
  bPct: number;
  cPct: number;
}

export function abcMetric(data: AbcRuleData): AbcMetric {
  return data.classification.mode === "threshold"
    ? data.classification.metric
    : "revenue";
}

export function rulePercents(data: AbcRuleData): TierPercents {
  return data.classification.mode === "threshold"
    ? {
        aPct: data.classification.a_pct,
        bPct: data.classification.b_pct,
        cPct: data.classification.c_pct,
      }
    : DEFAULT_SMART_PERCENTS;
}

export function assignTiers(
  productToMetric: Map<bigint, number>,
  { aPct, bPct }: TierPercents,
): Map<bigint, Tier> {
  const ranked = [...productToMetric.entries()].sort((a, b) => b[1] - a[1]);
  const total = ranked.length;
  const aCount = Math.round((aPct / 100) * total);
  const bCount = Math.round((bPct / 100) * total);

  const productToTier = new Map<bigint, Tier>();
  for (const [index, [productId]] of ranked.entries()) {
    const tier: Tier =
      index < aCount ? "A" : index < aCount + bCount ? "B" : "C";
    productToTier.set(productId, tier);
  }
  return productToTier;
}

export function dueTiers(data: AbcRuleData, days: number): Set<Tier> {
  const due = new Set<Tier>();
  if (days < 0) {
    return due;
  }
  // Exact-anchor firing is intentional: a missed cron day defers that tier to
  // its next multiple, and the following anchor day recovers it. Don't replace
  // this with a catch-up window (days % tierDays < N) — open-count dedup only
  // skips OPEN counts, so a same-period count that already completed would be
  // re-created the next day.
  if (days % data.a_days === 0) {
    due.add("A");
  }
  if (days % data.b_days === 0) {
    due.add("B");
  }
  if (days % data.c_days === 0) {
    due.add("C");
  }
  return due;
}

function tierDays(data: AbcRuleData, tier: Tier): number {
  switch (tier) {
    case "A":
      return data.a_days;
    case "B":
      return data.b_days;
    case "C":
      return data.c_days;
  }
}

export function buildAbcCycleCounts(
  ctx: BuildContext<AbcRule>,
  products: CycleCountProduct[],
): CycleCount[] {
  const { rule, now } = ctx;
  const data = rule.data;
  const days = daysSinceLive(rule.liveAt, now);
  if (days < 0) {
    return [];
  }
  const seedRun = days === 0;
  const due = dueTiers(data, days);
  if (!seedRun && due.size === 0) {
    return [];
  }

  const productToTier = assignTiers(
    new Map(products.map((product) => [product.productId, product.metric])),
    rulePercents(data),
  );

  const included = products.filter((product) => {
    if (product.hasOpenCount) {
      return false;
    }
    const tier = productToTier.get(product.productId);
    if (!tier) {
      return false;
    }
    return seedRun
      ? isStaleBeyond(product.lastCountedAt, tierDays(data, tier), now)
      : due.has(tier);
  });

  return groupProductsByLocation(included);
}
